import { type ChallengeMetric, toggleToday } from '@dovefiniscono/core/game/challenges';
import { useGame } from '@dovefiniscono/core/game/GameProvider';
import {
  type Badge,
  type Game,
  type Quest,
  type Source,
  TIER_NAMES,
} from '@dovefiniscono/core/game/game';
import { today as todayISO } from '@dovefiniscono/core/lib/dates';
import { Link } from '@tanstack/react-router';
import { cx } from '../../lib/cx';
import { useCollection, useStore } from '../../lib/data';
import type { Tone } from '../../lib/sections';
import { Icon } from '../../ui/Icon';
import styles from './Game.module.css';

export const SOURCE_LABEL: Record<Source, string> = {
  focus: 'Focus',
  attivita: 'Attività',
  calendario: 'Calendario',
  sfide: 'Sfide',
  distintivi: 'Distintivi',
};

/** Each source wears the colour of its line; challenges the red of "now". */
export const SOURCE_TONE: Record<Source | 'settimana', Tone> = {
  focus: 'focus',
  attivita: 'attivita',
  calendario: 'calendario',
  sfide: 'oggi',
  distintivi: 'focus',
  settimana: 'oggi',
};

/** Your challenges wear the colour of what measures them; the ones you tick, the red of "now". */
export const MINE_TONE: Record<ChallengeMetric, Tone> = {
  manual: 'oggi',
  focusMinutes: 'focus',
  sessions: 'focus',
  tasks: 'attivita',
  onTime: 'attivita',
  units: 'attivita',
  blocksKept: 'calendario',
  recordedDays: 'finanze',
};

const number = (value: number) => value.toLocaleString('it-IT');

/** Level, title and the way to the next level. */
export function LevelMeter({ game }: { game: Game }) {
  return (
    <div className={styles.meter}>
      <p className={styles.meterHead}>
        <span className={cx('num', styles.meterLevel)}>{game.level}</span>
        <span className={styles.meterRank}>
          <span className={styles.meterLabel}>Livello</span>
          {game.rank}
        </span>
      </p>
      <span className={styles.bar} aria-hidden="true">
        <span className={styles.barFill} style={{ width: `${game.progress * 100}%` }} />
      </span>
      <p className={cx('num', styles.meterMeta)}>
        {number(game.points)} / {number(game.next)} punti
        {game.nextRank ? ` · ${game.nextRank.title} al livello ${game.nextRank.from}` : ''}
      </p>
    </div>
  );
}

/** Where the points came from, line by line. */
export function PointsBreakdown({ game }: { game: Game }) {
  const sources = Object.keys(game.bySource) as Source[];
  const widest = Math.max(1, ...sources.map((source) => game.bySource[source]));
  return (
    <ul className={styles.breakdown}>
      {sources.map((source) => (
        <li key={source} data-tone={SOURCE_TONE[source]} className={styles.breakdownRow}>
          <span className={styles.breakdownName}>{SOURCE_LABEL[source]}</span>
          <span className={styles.breakdownTrack} aria-hidden="true">
            <span
              className={styles.breakdownFill}
              style={{ width: `${(game.bySource[source] / widest) * 100}%` }}
            />
          </span>
          <span className={cx('num', styles.breakdownValue)}>{number(game.bySource[source])}</span>
        </li>
      ))}
    </ul>
  );
}

/** A badge as a metro sign: its grade in Roman numerals, the plate darker as it grows. */
export function BadgeSign({ badge, size = 'md' }: { badge: Badge; size?: 'sm' | 'md' }) {
  return (
    <span
      className={cx(styles.sign, size === 'sm' && styles.signSmall)}
      data-tier={badge.tier}
      data-tone={SOURCE_TONE[badge.section]}
      aria-hidden="true"
    >
      {/* Not earned yet: the first grade, faded, rather than an empty box to tick. */}
      {TIER_NAMES[Math.max(0, badge.tier - 1)]}
    </span>
  );
}

const SECTIONS: { section: Badge['section']; title: string }[] = [
  { section: 'focus', title: 'Focus' },
  { section: 'attivita', title: 'Attività' },
  { section: 'calendario', title: 'Calendario' },
  { section: 'sfide', title: 'Sfide' },
];

export function BadgeGrid({ badges }: { badges: readonly Badge[] }) {
  return (
    <div className={styles.badgeGroups}>
      {SECTIONS.map(({ section, title }) => (
        <section key={section} className={styles.badgeGroup} data-tone={SOURCE_TONE[section]}>
          <h3 className={styles.badgeGroupTitle}>{title}</h3>
          <ul className={styles.badges}>
            {badges
              .filter((badge) => badge.section === section)
              .map((badge) => (
                <li key={badge.id} className={styles.badge}>
                  <BadgeSign badge={badge} />
                  <span className={styles.badgeBody}>
                    <span className={styles.badgeTitle}>
                      {badge.title}
                      {badge.tier > 0 ? (
                        <span className={styles.badgeTier}>
                          {' '}
                          · grado {TIER_NAMES[badge.tier - 1]}
                        </span>
                      ) : null}
                    </span>
                    <span className={cx('num', styles.badgeMeta)}>
                      {badge.next === null
                        ? `Completo: ${number(badge.value)} ${badge.unit}`
                        : `${number(badge.value)} / ${number(badge.next)} ${badge.unit}`}
                    </span>
                    <span className={styles.bar} aria-hidden="true">
                      <span className={styles.barFill} style={{ width: `${badge.ratio * 100}%` }} />
                    </span>
                  </span>
                </li>
              ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * A challenge fills by itself as you use the app: a dot and a bar, never a box that looks like
 * something to tick.
 */
function QuestMark({ met }: { met: boolean }) {
  return (
    <span className={styles.questMark} aria-hidden="true">
      {met ? <Icon name="check" /> : null}
    </span>
  );
}

/**
 * A challenge in a row. The ones you tick yourself get a real checkbox (for today) when
 * `onTick` is given; all the others fill by themselves.
 */
export function QuestRow({
  quest,
  past = false,
  onTick,
}: {
  quest: Quest;
  past?: boolean;
  onTick?: () => void;
}) {
  const tickable = quest.mine?.metric === 'manual' && onTick !== undefined;
  const tone = quest.mine ? MINE_TONE[quest.mine.metric] : SOURCE_TONE[quest.section];
  return (
    <li
      className={cx(styles.quest, quest.met && styles.questMet)}
      data-tone={tone}
      title={
        tickable
          ? 'Spuntala quando l’hai fatta'
          : quest.met
            ? 'Fatta'
            : 'Si completa da sola mentre usi l’app'
      }
    >
      {tickable ? (
        <input
          type="checkbox"
          className={styles.questCheck}
          checked={quest.mine?.tickedToday ?? false}
          onChange={onTick}
          aria-label={
            quest.mine?.tickedToday
              ? `Togli la spunta di oggi: ${quest.title}`
              : `Fatta oggi: ${quest.title}`
          }
        />
      ) : (
        <QuestMark met={quest.met} />
      )}
      <span className={styles.questTitle}>
        {quest.title}
        <span className="visually-hidden">
          {quest.met ? ', completata' : past ? ', non completata' : ''}
        </span>
      </span>
      <span className={styles.questBar} aria-hidden="true">
        <span
          className={styles.questBarFill}
          style={{ width: `${Math.min(1, quest.done / Math.max(1, quest.target)) * 100}%` }}
        />
      </span>
      <span className={cx('num', styles.questCount)}>
        {quest.done}/{quest.target}
      </span>
      <span className={cx('num', styles.questPoints)}>+{quest.points}</span>
    </li>
  );
}

/** Today's three challenges, the week's one, and your own. */
export function QuestList({ game }: { game: Game }) {
  const store = useStore();
  const challenges = useCollection('challenges');
  const today = todayISO();
  const tick = (quest: Quest) => {
    const challenge = challenges.find((item) => item.id === quest.mine?.challengeId);
    if (challenge) {
      store.update('challenges', challenge.id, {
        doneDays: toggleToday(challenge.doneDays, today),
      });
    }
  };
  return (
    <div className={styles.questBlock}>
      <ul className={styles.quests}>
        {game.quests.map((quest) => (
          <QuestRow key={quest.id} quest={quest} />
        ))}
      </ul>
      <p className={styles.questNote}>
        {game.perfectToday
          ? 'Giornata perfetta: +25 punti in più.'
          : 'Si completano da sole mentre usi l’app. Tutte e tre: giornata perfetta, +25 punti in più.'}
      </p>
      {game.myDaily.length > 0 ? (
        <>
          <p className={styles.questWeekLabel}>Le tue sfide</p>
          <ul className={styles.quests}>
            {game.myDaily.map((quest) => (
              <QuestRow key={quest.id} quest={quest} onTick={() => tick(quest)} />
            ))}
          </ul>
        </>
      ) : null}
      <p className={styles.questWeekLabel}>Settimana</p>
      <ul className={styles.quests}>
        <QuestRow quest={game.weekly} />
        {game.myWeekly.map((quest) => (
          <QuestRow key={quest.id} quest={quest} onTick={() => tick(quest)} />
        ))}
      </ul>
      <p className={styles.questNote}>
        <Link to="/focus" hash="sfide">
          {game.myDaily.length + game.myWeekly.length > 0 ? 'Le tue sfide' : 'Crea le tue sfide'}
        </Link>
      </p>
    </div>
  );
}

/**
 * The game in one line at the top of a section: its challenge of the day, the points of today and
 * the closest badge of that line.
 */
export function GameStrip({ section }: { section: 'attivita' | 'calendario' }) {
  const game = useGame();
  const quest = game.quests.find((item) => item.section === section);
  const closest = game.badges
    .filter((badge) => badge.section === section && badge.next !== null)
    .sort((a, b) => b.ratio - a.ratio)[0];
  return (
    <div className={styles.strip} data-tone={SOURCE_TONE[section]}>
      {quest ? (
        <span
          className={cx(styles.stripItem, quest.met && styles.stripDone)}
          title={quest.met ? 'Fatta' : 'Si completa da sola mentre usi l’app'}
        >
          <QuestMark met={quest.met} />
          <span className={styles.stripLabel}>Sfida di oggi</span>
          {quest.title}
          <span className="num">
            {' '}
            · {quest.done}/{quest.target}
          </span>
        </span>
      ) : null}
      <span className={styles.stripItem}>
        <span className={styles.stripLabel}>Oggi</span>
        <span className="num">+{number(game.today[section])} punti</span>
      </span>
      {closest ? (
        <span className={styles.stripItem}>
          <BadgeSign badge={closest} size="sm" />
          <span className={styles.stripLabel}>Prossimo distintivo</span>
          {closest.title} {TIER_NAMES[closest.tier]}
          <span className="num">
            {' '}
            · {number(closest.value)}/{number(closest.next ?? 0)}
          </span>
        </span>
      ) : null}
      <Link to="/focus" hash="progressi" className={styles.stripLink}>
        Livello {game.level} · {game.rank}
      </Link>
    </div>
  );
}

/** Level in the sidebar, under the line. */
export function LevelChip() {
  const game = useGame();
  return (
    <Link to="/focus" hash="progressi" className={styles.chip} data-tone="focus">
      <span className={styles.chipHead}>
        <span className={cx('num', styles.chipLevel)}>Livello {game.level}</span>
        <span className={styles.chipRank}>{game.rank}</span>
      </span>
      <span className={styles.bar} aria-hidden="true">
        <span className={styles.barFill} style={{ width: `${game.progress * 100}%` }} />
      </span>
      <span className={cx('num', styles.chipMeta)}>
        {game.streak > 0
          ? `${game.streak} ${game.streak === 1 ? 'giorno' : 'giorni'} di fila · `
          : ''}
        {game.quests.filter((quest) => quest.met).length}/3 sfide
      </span>
    </Link>
  );
}
